import JDILogin from '@/app/login/index.login';
import isInWhiteList from '@/constants/attributionWhiteList';
import API_CODE_TYPE from '@/constants/enumAPICodeType';
import { getI18nLang } from '@/i18n/settings';
import { getSSrAddressBody } from '@/utils/addressUtils';
import cookieUtil from '@/intl-jdi-common-ssr/utils/cookieUtils';
import getDeviceInfoFromUA from '@/utils/deviceInfo';
import { getEdiTokenFromCookie } from '@/utils/fingerprint';
import { safeJsonParse } from '@/utils/index';
import { logger } from '@/utils/logger';
import { getColorApiPath, getJDISiteKeys } from '@/utils/multiSite/index';
import { MONITOR_CODE_TYPE } from '@/utils/sgmUtils';
import { getGAClientId, getGASessionId } from '@/intl-jdi-common-ssr/utils/thirdParty/gtag';
import { getPageUrl, isNextClient, isNextServer } from '@/intl-jdi-common-ssr/utils/uaParser';
import fetchToCurl from 'fetch-to-curl';
import { isExcludeUa } from '@/utils/logger/utils';
import { appidClient, appidServer } from '@/constants/colorApi';

type Json = string | number | boolean | null | { [property: string]: Json } | Json[] | any;

interface RequestInitWithBodyQuery extends RequestInit {
  bodyQuery?: Json;
  body?: any;
  params?: any;
  cthr?: string;
}

const FALLBACK_IP_ADDRESS = '0.0.0.0';
const Default_UA =
  'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/136.0.0.0 Safari/537.36';

const isBrowser = isNextClient();
const isServer = isNextServer();

const COLOR_URL = getColorApiPath();
const BASE_URL = isBrowser ? window.location.origin : undefined;

const timeout = 10000;

const reqIntercept_SetTimeout = (options: RequestInit = {}) => {
  if (isBrowser && (!window.AbortSignal || !window.AbortSignal.timeout)) {
    console.log('window.AbortSignal.timeout not support!');
  } else {
    options = Object.assign({ signal: AbortSignal.timeout(timeout) }, options);
  }
  return { options };
};

const getUserRealIp = () => {
  const { headers } = require('next/headers');
  const forwardedFor = headers().get('J-Forwarded-For');
  logger.info({
    eventName: 'user_real_ip_get',
    from: 'getUserRealIp',
    forwardedFor,
    'x-real-ip': headers().get('x-real-ip'),
  });

  if (forwardedFor) {
    return forwardedFor.split(',')[0] ?? FALLBACK_IP_ADDRESS;
  }

  return headers().get('x-real-ip') ?? FALLBACK_IP_ADDRESS;
};

const reqIntercept_AddAttributionParams = (
  functionId: string,
  options: RequestInitWithBodyQuery = {},
) => {
  if (isInWhiteList(functionId)) {
    // 获取用户归因
    let attribution = '';
    if (isServer) {
      const { cookies } = require('next/headers');
      attribution = cookies().get('_attribution');
    } else {
      attribution = cookieUtil.getCookie('_attribution');

      // safeJsonParse会默认返回一个{}, 导致没有cookie._attribution的时候也会有值，需要过滤
      if (attribution) {
        const attributionJson = safeJsonParse(attribution);
        if (getGASessionId() && attributionJson?.utm) {
          attributionJson.ga4_session_id = getGASessionId();
        }
        if (getGAClientId() && attributionJson?.utm) {
          attributionJson.client_id = getGAClientId();
        }
        attributionJson.client_report_google_ads = true;

        attribution = JSON.stringify(attributionJson);
      }
    }
    if (attribution) {
      if (options.bodyQuery) {
        options.bodyQuery.launchAttribution = attribution;
      } else {
        options.bodyQuery = { launchAttribution: attribution };
      }
    }
  }
  return { options };
};

const reqIntercept_AddCommonParams = (
  functionId: string,
  options: RequestInitWithBodyQuery = {},
  isCommonArea: boolean,
  needUniformBizInfo: boolean,
  needDeviceInfo: boolean,
  usePostBody: boolean = false,
) => {
  let cookieString = '';
  if (isServer) {
    const { cookies } = require('next/headers');
    cookieString = cookies().toString();
  } else {
    cookieString = document?.cookie;
  }

  const deviceInfoParams = needDeviceInfo && getDeviceInfoFromUA();
  const originUrlSearchParams = new URLSearchParams(options.params);
  const defaultUrlSearchParams = new URLSearchParams({
    functionId,
    appid: isServer ? appidServer : appidClient,
    loginType: '30',
    client: 'pc',
    clientVersion: '1.0.0',
    // t参数每次请求的时候都会变化，导致fetch的cache失效，所以拼接url的时候不要t
    // t: String(new Date().getTime()),
    'x-api-eid-token': getEdiTokenFromCookie() || '',
    ...deviceInfoParams,
  });

  let bodyQuerySearchParams = new URLSearchParams({});
  if (!options.bodyQuery) {
    options.bodyQuery = {};
  }

  if (isCommonArea) {
    const addressBody = getSSrAddressBody(cookieString);
    Object.assign(options.bodyQuery as any, addressBody);
  }

  if (needUniformBizInfo) {
    Object.assign(options.bodyQuery as any, {
      uniformBizInfo: {
        tenantId: 1,
        buId: 405,
        channelId: 0,
        ua: 4,
      },
    });
  }
  bodyQuerySearchParams = new URLSearchParams({ body: JSON.stringify(options.bodyQuery) });

  const urlObj = new URL(COLOR_URL, BASE_URL);

  if (usePostBody) {
    // encodeURIComponent 解决参数名中含有&等特殊符号，造成解析错误
    options.body = `body=${encodeURIComponent(JSON.stringify(options.bodyQuery))}`;
    urlObj.search = new URLSearchParams({
      ...Object.fromEntries(defaultUrlSearchParams),
      ...Object.fromEntries(originUrlSearchParams),
    }).toString();
  } else {
    urlObj.search = new URLSearchParams({
      ...Object.fromEntries(defaultUrlSearchParams),
      ...Object.fromEntries(bodyQuerySearchParams),
      ...Object.fromEntries(originUrlSearchParams),
    }).toString();
  }

  return { url: urlObj.toString(), options };
};

const reqIntercept_AddHeaders = async (
  url: string,
  options: RequestInit = {},
  usePostBody = false,
) => {
  const headers = new Headers(options.headers);
  const langCookie = getI18nLang();
  //   const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const JDIHeaders = getJDISiteKeys(true);

  headers.append('x-api-platform', 'pc');
  headers.append('x-requested-with', 'XMLHttpRequest');
  headers.append('origin', 'https://www.joybuy.com');
  headers.append('JDI-Lang', langCookie);
  // ab实验要用的通用参数，后端需要这个参数去接试金石去分流
  headers.append('x-api-device-token', getEdiTokenFromCookie());
  headers.append('uuid', getEdiTokenFromCookie());
  //   headers.append("JDI-Timezone", timeZone);
  if (usePostBody) {
    headers.append('Content-Type', 'application/x-www-form-urlencoded');
  }

  for (const key in JDIHeaders) {
    headers.append(key, JDIHeaders[key as keyof typeof JDIHeaders]);
  }

  if (isServer) {
    // 获取用户cookie
    const { cookies, headers: nextHeaders } = require('next/headers');

    const cookieStore = cookies();
    const cookieString = cookieStore.toString();

    headers.append('Cookie', cookieString);
    // 获取特性环境标识
    const swimlane = nextHeaders().get('swimlane');
    if (swimlane) {
      headers.append('swimlane', swimlane);
    }
    // 获取用户真实ip
    const userIp = getUserRealIp();
    headers.append('X-Proxy-Client-Ip', userIp);
    if (!headers.get('User-Agent')) {
      if (userIp === FALLBACK_IP_ADDRESS) {
        // 内网请求切没有设置ua时，透传一个固定的ua，用于绕过color的风控，否则curl的ua会被拦截
        headers.append('User-Agent', Default_UA);
      } else {
        headers.append('User-Agent', nextHeaders().get('User-Agent'));
      }
    }
  }
  options.headers = headers;
  options.credentials = 'include';

  return { url, options };
};

const resIntercept_authorizationCheck = async (res: any, config: ColorFetchConfig) => {
  // const responseData = await res.json();
  // 此处暂时不做处理，因为未登陆态，前端以弹窗形式呈现登录框，而非打开登录页。
  // tips: 业务401 异常处理
  // console.log("resIntercept_authorizationCheck", res);
  if (res.code === API_CODE_TYPE.NOT_LOGIN) {
    if (isBrowser) {
      if (config?.ignoreForceLogin) {
        // 跳过未登录重定向
        return;
      }
      JDILogin.getInstance().openLoginDialog();
    }
  } else if (res.code === '403') {
    if (isBrowser && !window.ignore403Redirect) {
      // 确保在浏览器
      const fullPath = window.location.pathname + window.location.search + window.location.hash;
      location.href = `/error403?referrer=${encodeURIComponent(fullPath)}`;
    } else {
      const { headers: nextHeaders } = require('next/headers');
      const pathname = nextHeaders().get('pathname');
      if (pathname !== '/error403') {
        throw new Error('risk-control-403');
      }
    }
  }
};

interface ColorFetchConfig {
  isCommonArea?: boolean;
  needUniformBizInfo?: boolean; // 是否需要交易四要素
  ignoreForceLogin?: boolean; // 是否忽略强制登录
  needDeviceInfo?: boolean; // 是否需要设备信息
  usePostBody?: boolean;
}
const DefaultColorFetchConfig: ColorFetchConfig = Object.freeze({
  isCommonArea: true,
  needUniformBizInfo: true,
  ignoreForceLogin: false,
  needDeviceInfo: false,
  usePostBody: false,
});

// 参考 https://stackoverflow.com/questions/46946380/fetch-api-request-timeout 实现
export const jdiColorFetch = async (
  functionId: string,
  options: RequestInitWithBodyQuery = {},
  config: ColorFetchConfig = {},
) => {
  const { isCommonArea, needUniformBizInfo, needDeviceInfo, usePostBody } = {
    ...DefaultColorFetchConfig,
    ...config,
  };
  options = Object.assign({ next: { revalidate: 10 } }, options); // 所有的请求默认10s的缓存时间
  const requestId = Math.random().toString(36).slice(2);
  let url = COLOR_URL;
  // 新增归因参数
  ({ options } = reqIntercept_AddAttributionParams(functionId, options));
  // 新增超时时间
  ({ options } = reqIntercept_SetTimeout(options));
  // 新增通用参数和bodyQuery里的业务参数
  ({ url, options } = reqIntercept_AddCommonParams(
    functionId,
    options,
    isCommonArea as boolean,
    needUniformBizInfo as boolean,
    needDeviceInfo as boolean,
    usePostBody,
  ));
  // 新增通用header
  ({ url, options } = await reqIntercept_AddHeaders(url, options, usePostBody));

  logger.info({
    eventName: MONITOR_CODE_TYPE.API_REQ,
    functionId,
    isExcludeUa: isExcludeUa(),
    msg: {
      requestId,
      curlCommand: fetchToCurl(url, options),
    },
  });

  try {
    // 发起请求
    const response = await fetch(url, options);
    if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') {
      logger.info({
        eventName: MONITOR_CODE_TYPE.API_CURL,
        msg: { requestId, curlCommand: fetchToCurl(url, options) },
      });
    }

    const XApiRequestId = response.headers.get('x-api-request-id');
    // const responseCloned = response.clone();
    const responseData = await response.json();

    // 401鉴权处理
    await resIntercept_authorizationCheck(responseData, config);

    if (responseData.code === API_CODE_TYPE.SUCCESS) {
      logger.info({
        eventName: MONITOR_CODE_TYPE.API_SUCCESS,
        functionId,
        isExcludeUa: isExcludeUa(),
        msg: {
          requestId,
          XApiRequestId,
          traceId: responseData.traceId,
          userIP: !isNextClient() && getUserRealIp(),
        },
      });
    } else {
      // 接口报错打印日志
      logger.error({
        eventName: MONITOR_CODE_TYPE.API_FAILED,
        functionId,
        // sgm自定义事件名与接口返回code字段名相同，避免字段冲突改为errorCode
        errorCode: responseData?.code,
        resultCode: responseData?.resultCode,
        isExcludeUa: isExcludeUa(),
        msg: {
          requestId,
          userIP: !isNextClient() && getUserRealIp(),
          pageUrl: getPageUrl(),
          traceId: responseData.traceId,
          data: JSON.stringify(responseData),
          XApiRequestId,
          curlCommand: fetchToCurl(url, options),
        },
      });
    }

    return responseData;
  } catch (error) {
    logger.error({
      eventName: MONITOR_CODE_TYPE.FETCH_FAILED,
      functionId,
      isExcludeUa: isExcludeUa(),
      msg: {
        requestId,
        userIP: !isNextClient() && getUserRealIp(),
        pageUrl: getPageUrl(),
        method: options.method,
        error,
        curlCommand: fetchToCurl(url, options),
      },
    });
    if (error.message === 'risk-control-403') {
      const { redirect } = require('next/navigation');
      const { headers: nextHeaders } = require('next/headers');
      const pathname = nextHeaders().get('pathname') === null ? '/' : nextHeaders().get('nextUrl');
      return redirect(`/error403?referrer=${encodeURIComponent(pathname)}`);
    }
    return { error: error };
  }
};
